import { ReadingUnitType, TxtItemType } from './reading-utils/enums';
import { Rectangle } from './interfaces';
import { isRectContainsPoint } from './common';

export const punctSigns = ['.', '!', '?', ',', '@', '#', '$', '%', '^',
    '&', '*', '(', ')', '_', '=', '+', ':', ';',
    '"', '~', '`', '‘', '’', '\'', '<', '>', '[',
    ']', '{', '}', '\u200B'];
export const titles = new Set([
    'mr.', 'mrs.', 'ms.', 'dr.', 'prof.', 'sr.', 'jr.', 'st.', 'capt.', 'rev.'
]);
export const msspldCls = 'asg-splellcheck-wrong';
export const prdctdCls = 'asg-predicted-word';

export enum SpellCheckWordStatus {
    PROCESSING = 'PROCESSING',
    INVALID = 'INVALID',
    VALID = 'VALID',
    UNPROCESSED = 'UNPROCESSED',
}

interface IDictLanguage { lang: string; begin: number; end: number; }
interface ITxtDescription { text: string | string[]; paragraphs: number[]; languages: IDictLanguage[]; }
export interface INodeDescription { wordNode: Node; globalNodeOffset: number; insideNodeOffset: number; localTxtLength: number; }
export interface ITxtmapItem {
    symbPos: number; itemIndex: number; sntncIndex: number; txtSize: number; content: string; type: TxtItemType;
    status: SpellCheckWordStatus; lang: string; hasToBeRechecked?: boolean;
}

export class HtmlTextWalker {
    private readonly txtMap: ITxtmapItem[] = [];
    private readonly wordsCount: number = 0;
    private readonly sentencesCount: number = 0;

    constructor(private readonly rootNode: Node, private readonly targetDoc: Document) {
        this.txtMap = this.buildTxtMap();
        if (this.txtMap.length) {
            this.sentencesCount = this.txtMap[this.txtMap.length - 1].sntncIndex + 1;
            const lastWord = this.getPrevUnit(this.txtMap.length - 1, ReadingUnitType.Word);
            this.wordsCount = lastWord.length > 0 ? lastWord[0].itemIndex + 1 : 0;
        }
    }

    private get selection(): Selection | null { return this.targetDoc.getSelection(); }

    get sentencesNum(): number { return this.sentencesCount; }
    get wordsNum(): number { return this.wordsCount; }
    get textMap(): ITxtmapItem[] { return this.txtMap.concat(); }

    getItemsByNode(node: Node, offset: number, type: ReadingUnitType): ITxtmapItem[] | null {
        const resultItem = this.getItemByNode(node, offset);

        if (!resultItem) {
            return null;
        }

        if (type === ReadingUnitType.Word) {
            return [resultItem];
        } else if (type === ReadingUnitType.Sentence) {
            return this.getSentenceByIdx(resultItem.sntncIndex);
        }

        return null;
    }

    getClosestItemsByNode(node: Node, offset: number, type: ReadingUnitType): ITxtmapItem[] | null {
        const offsetObj = {
            offset: 0,
        };

        let resultItem = this.getItemByNode(node, offset, offsetObj);

        if (!resultItem) {
            const distances = this.txtMap.map(x => x.symbPos - offsetObj.offset);
            const index = distances.findIndex(x => x >= 0);

            if (index <= -1) {
                return null;
            }

            resultItem = this.txtMap[index];
        }

        if (type === ReadingUnitType.Word) {
            return [resultItem];
        } else if (type === ReadingUnitType.Sentence) {
            return this.getSentencePart(resultItem.itemIndex);
        }

        return null;
    }

    getSelectionOffset(): { globalOffset: number, localOffset: number } | null {
        if (!this.textMap.length) {
            return null;
        }

        const walker = this.getTreeWalker(NodeFilter.SHOW_TEXT);

        let globalOffset = 0;

        let node: Node | null = null;
        let localOffset: number = 0;

        if (this.selection) {
            const { anchorNode, anchorOffset } = this.selection;

            node = anchorNode;
            localOffset = anchorOffset;
        }

        while (walker.nextNode() !== null && !walker.currentNode.isSameNode(node)) {
            if (node && node.textContent && node.textContent.length === 0 && node.nodeType !== Node.TEXT_NODE &&
                walker.currentNode && walker.currentNode.compareDocumentPosition(node) === Node.DOCUMENT_POSITION_PRECEDING) {
                break;
            }

            if (walker.currentNode && walker.currentNode.textContent) {
                globalOffset += walker.currentNode.textContent.length;
            }
        }

        return { globalOffset, localOffset };
    }

    setSelectionOffset(dstGlobalOffset: number, dstLocalOffset: number): void {
        if (!this.textMap.length) {
            return;
        }

        const walker = this.getTreeWalker(NodeFilter.SHOW_TEXT);
        let srcGlobalOffset = 0;

        while (walker.nextNode() !== null) {
            if (walker.currentNode && walker.currentNode.textContent) {
                srcGlobalOffset += walker.currentNode.textContent.length;
            }

            if (srcGlobalOffset >= (dstGlobalOffset + dstLocalOffset)) {
                if (dstLocalOffset === 0 && srcGlobalOffset === (dstGlobalOffset + dstLocalOffset)) {
                    continue;
                }
                break;
            }
        }

        const node = walker.currentNode;

        if (node && node.textContent) {
            const offsetInsideNode = node.textContent.length - (srcGlobalOffset - (dstGlobalOffset + dstLocalOffset));

            if (offsetInsideNode > node.textContent.length) {
                return;
            }

            const range = document.createRange();
            range.setStart(node, offsetInsideNode);

            const selection = this.getCurrentSelection();

            if (selection) {
                selection.removeAllRanges();
                selection.addRange(range);
            }
        }
    }

    getMapItemByIdx(wordIdx: number): ITxtmapItem {
        return this.txtMap[wordIdx];
    }

    getSentenceByIdx(sntncIndex: number): ITxtmapItem[] {
        return this.txtMap.filter(x => x.sntncIndex === sntncIndex);
    }

    getLastWord(): ITxtmapItem | null {
        const reversedArr = this.txtMap.reverse();

        for (const itm of reversedArr) {
            if (itm.type === TxtItemType.Word) {
                return itm;
            }
        }

        return null;
    }

    getSentencePart(startWordIdx: number): ITxtmapItem[] {
        return this.txtMap.filter(x => x.sntncIndex === this.txtMap[startWordIdx].sntncIndex && x.itemIndex >= startWordIdx);
    }

    getNextUnit(itmIdx: number, type: ReadingUnitType): ITxtmapItem[] {
        if (type === ReadingUnitType.Word) {
            while (++itmIdx < this.txtMap.length && this.txtMap[itmIdx].type !== TxtItemType.Word) { }
            return itmIdx === this.txtMap.length ? [] : [this.txtMap[itmIdx]];
        } else if (type === ReadingUnitType.Sentence) {
            const sntncIndex = this.txtMap[itmIdx].sntncIndex;
            return sntncIndex < this.sentencesCount - 1 ? this.getSentenceByIdx(sntncIndex + 1) : [];
        }

        return [];
    }

    getPrevUnit(itmIdx: number, type: ReadingUnitType): ITxtmapItem[] {
        if (type === ReadingUnitType.Word) {
            while (--itmIdx >= 0 && this.txtMap[itmIdx].type !== TxtItemType.Word) { }
            return itmIdx >= 0 ? [this.txtMap[itmIdx]] : [];
        } else if (type === ReadingUnitType.Sentence) {
            const sntncIndex = this.txtMap[itmIdx].sntncIndex;
            return sntncIndex > 0 ? this.getSentenceByIdx(sntncIndex - 1) : [];
        }

        return [];
    }

    getWordRange(itmIdx: number): Range {
        const dstRange = this.targetDoc.createRange();
        const txtItm = this.txtMap[itmIdx];
        const nodeData = this.getWordTextNodes(txtItm)[0];
        const anchorOffset = txtItm.symbPos - nodeData.globalNodeOffset;
        const walker = this.getTreeWalker(NodeFilter.SHOW_TEXT, nodeData.wordNode);

        dstRange.setStart(walker.currentNode, anchorOffset);

        if (walker.currentNode && walker.currentNode.textContent) {
            if (txtItm.txtSize > walker.currentNode.textContent.length - anchorOffset) { // word finished in next node
                let nxtNodeTxtSize = txtItm.txtSize - (walker.currentNode.textContent.length - anchorOffset);
                while (walker.nextNode() !== null && walker.currentNode.textContent.length <= nxtNodeTxtSize) {
                    nxtNodeTxtSize -= walker.currentNode.textContent.length;
                }

                dstRange.setEnd(walker.currentNode, nxtNodeTxtSize);
            } else {
                dstRange.setEnd(walker.currentNode, anchorOffset + txtItm.txtSize);
            }
        }

        return dstRange;
    }

    getSentenceRange(sPos: number, fPos: number): Range {
        const dstRange = this.targetDoc.createRange();

        const startNode = this.getWordTextNodes(this.txtMap[sPos])[0];
        const finishNode = this.getWordTextNodes(this.txtMap[fPos])[0];

        dstRange.setStart(startNode.wordNode, this.txtMap[sPos].symbPos - startNode.globalNodeOffset);
        dstRange.setEnd(finishNode.wordNode, (this.txtMap[fPos].txtSize + this.txtMap[fPos].symbPos) - finishNode.globalNodeOffset);

        return dstRange;
    }

    getWordsFromRange(targetRange: Range): ITxtmapItem[] {
        /* triming range region */
        let indexBefore = 0;
        let indexAfter = 0;
        let text = targetRange.toString();
        const empties = [32, 160, 67];
        let textLen = text.length - 1;
        while (text && empties.indexOf(text.charAt(0).charCodeAt(0)) > -1) {
            indexBefore++;
            text = text.substr(1);
            textLen--;
        }
        while (text && empties.indexOf(text.charAt(textLen).charCodeAt(0)) > -1) {
            indexAfter++;
            text = text.slice(0, -1);
            textLen--;
        }
        /* end triming range region */
        const beginItms = this.getItemsByNode(
            targetRange.startContainer,
            indexBefore !== 0 ? targetRange.startOffset + indexBefore : targetRange.startOffset,
            ReadingUnitType.Word
        );
        const endItms = this.getItemsByNode(
            targetRange.endContainer,
            indexAfter !== 0 ? targetRange.endOffset - indexAfter : targetRange.endOffset,
            ReadingUnitType.Word
        );

        if (beginItms !== null && endItms !== null && beginItms.length > 0 && endItms.length > 0) {
            return this.txtMap.filter(x => x.itemIndex >= beginItms[0].itemIndex && x.itemIndex <= endItms[endItms.length - 1].itemIndex);
        } else {
            return [];
        }
    }

    getPredictedWordWrapper(itmIdx: number): Node | null {
        const nodes = this.getWordTextNodes(this.txtMap[itmIdx]);
        const walker = this.getTreeWalker(NodeFilter.SHOW_ALL);

        const isTarget = (srcNd: Node, cmprNd: Node) => {
            return srcNd.nodeType === Node.ELEMENT_NODE && srcNd.contains(cmprNd) && ((srcNd as HTMLElement).classList.contains(prdctdCls)
                || (srcNd as HTMLElement).classList.contains(msspldCls));
        };

        while (walker.nextNode() !== null && !isTarget(walker.currentNode, nodes[0].wordNode)) { }

        return isTarget(walker.currentNode, nodes[0].wordNode) ? walker.currentNode : null;
    }

    getItemByPoint(x: number, y: number): ITxtmapItem | null {
        let mainNode: Node | null = null;
        const srcNode = this.targetDoc.elementFromPoint(x, y);

        const walker = this.getTreeWalker(NodeFilter.SHOW_ALL);
        while (walker.nextNode() !== null) {
            if (walker.currentNode.nodeType === Node.TEXT_NODE && srcNode &&
                srcNode.contains(walker.currentNode) && walker.currentNode.textContent === srcNode.textContent) {
                mainNode = walker.currentNode;
                break;
            }
        }

        const getBoundary = (srcWord: ITxtmapItem | null): Rectangle | null => {
            const range = srcWord !== null ? this.getWordRange(srcWord.itemIndex) : null;
            const bndry = range !== null ? range.getBoundingClientRect() : null;
            return bndry !== null ? { x: bndry.left, y: bndry.top, width: bndry.width, height: bndry.height } : null;
        };

        const itms = mainNode !== null ? this.getItemsByNode(mainNode, 0, ReadingUnitType.Word) : null;
        let word = itms !== null && itms.length > 0 ? itms[0] : null;
        let wordRect = getBoundary(word);

        while (word !== null && wordRect !== null) {
            if (isRectContainsPoint(x, y, wordRect)) {
                return word;
            }

            word = this.txtMap.find(e => (!!word && e.itemIndex > word.itemIndex) && e.type !== TxtItemType.Punctuation) || null;
            wordRect = word !== null ? getBoundary(word) : null;
        }

        return null;
    }

    getMisspeledWordWrapper(itmIdx: number): Node | null {
        const nodes = this.getWordTextNodes(this.txtMap[itmIdx]);

        if (!nodes || !nodes.length) {
            return null;
        }
        const walker = this.getTreeWalker(NodeFilter.SHOW_ELEMENT);
        const isTarget = (srcNd: Node, cmprNd: Node) => {
            return srcNd.nodeType === Node.ELEMENT_NODE && srcNd.contains(cmprNd) && (srcNd as HTMLElement).classList.contains(msspldCls);
        };

        while (walker.nextNode() !== null && !isTarget(walker.currentNode, nodes[0].wordNode)) { }

        return isTarget(walker.currentNode, nodes[0].wordNode) ? walker.currentNode : null;
    }

    markMisspeledWord(itmIdx: number, prediction: boolean = false): boolean {
        if (this.getMisspeledWordWrapper(itmIdx) !== null) {
            return false;
        }

        const nodes = this.getWordTextNodes(this.txtMap[itmIdx]);

        const newSpan = this.targetDoc.createElement('span');
        if (!prediction) {
            newSpan.classList.add(msspldCls);
        } else {
            newSpan.classList.add(prdctdCls);
        }

        for (const nodeData of nodes) {
            let targetSpan = newSpan;

            if (nodes.length > 1) {
                targetSpan = this.targetDoc.createElement('span');
                newSpan.appendChild(targetSpan);
            }

            if (nodeData.wordNode) {
                if (nodeData.wordNode.textContent) {
                    targetSpan.textContent = nodeData.wordNode.textContent.substr(nodeData.insideNodeOffset, nodeData.localTxtLength);
                }

                if (nodeData.wordNode.parentElement && nodeData.wordNode.parentElement.tagName.toLowerCase() !== 'p') {
                    this.mapFroalaStyles(nodeData.wordNode.parentElement, targetSpan, nodes.length === 1);
                }
            }
        }

        for (let i = 0; i < nodes.length; i++) {
            const nodeData = nodes[i];

            if (nodeData && nodeData.wordNode && nodeData.wordNode.textContent) {
                const txtAfterWord = nodeData.wordNode.textContent.substring(nodeData.insideNodeOffset + nodeData.localTxtLength);

                if (i === 0) {
                    if (nodeData.insideNodeOffset > 0) {
                        const txtBeforeWord = nodeData.wordNode.textContent.substring(0, nodeData.insideNodeOffset);

                        nodeData.wordNode.textContent = txtBeforeWord;

                        if (nodeData.wordNode.parentNode) {
                            nodeData.wordNode.parentNode.insertBefore(newSpan, nodeData.wordNode.nextSibling);
                            nodeData.wordNode.parentNode.insertBefore(this.targetDoc.createTextNode(txtAfterWord), newSpan.nextSibling);
                        }
                    } else {
                        nodeData.wordNode.textContent = txtAfterWord;

                        if (nodeData.wordNode.parentNode) {
                            nodeData.wordNode.parentNode.insertBefore(newSpan, nodeData.wordNode);
                        }
                    }
                } else {
                    nodeData.wordNode.textContent = txtAfterWord;
                }
            }
        }

        if (newSpan && newSpan.parentNode) {
            // Delay normalize() to avoid interfering with recent DOM manipulations.
            // Without this timeout, normalize() may merge text nodes too early,
            // causing style updates (e.g., font changes) to be lost or misapplied.
            setTimeout(() => { newSpan.parentNode?.normalize() }, 0);
        }

        return true;
    }

    getCurrentSelection(): Selection | null {
        return this.targetDoc.getSelection();
    }

    getWordTextNodes(wrdItm: ITxtmapItem): INodeDescription[] {
        const walker = this.getTreeWalker(NodeFilter.SHOW_TEXT);
        const wordNodes: any[] = [];
        let globalNodeOffset = 0; // first symbol position from begin of text

        while (walker.nextNode() !== null) {
            if (walker.currentNode && walker.currentNode.textContent) {
                const nodeSize = walker.currentNode.textContent.length;

                if ((nodeSize + globalNodeOffset) > wrdItm.symbPos) {
                    const insideNodeOffset = wrdItm.symbPos < globalNodeOffset ? 0 : wrdItm.symbPos - globalNodeOffset;
                    const symbAvailable = nodeSize - insideNodeOffset;
                    const symbBeforeNode = wrdItm.symbPos < globalNodeOffset ? globalNodeOffset - wrdItm.symbPos : 0;
                    const localTxtLength = (wrdItm.txtSize - symbBeforeNode) > symbAvailable ?
                        symbAvailable : wrdItm.txtSize - symbBeforeNode;

                    wordNodes.push({ wordNode: walker.currentNode, globalNodeOffset, insideNodeOffset, localTxtLength });
                }

                if ((wrdItm.symbPos + wrdItm.txtSize) <= (nodeSize + globalNodeOffset)) {
                    break;
                }

                globalNodeOffset += walker.currentNode.textContent.length;
            }
        }

        return wordNodes;
    }

    private mapFroalaStyles(srcElement: HTMLElement, dstElement: HTMLElement, skipDecoration: boolean = false): void {
        const srcStyle = window.getComputedStyle(srcElement);

        if (!skipDecoration) {
            dstElement.style.textDecoration = srcStyle.textDecoration;
        }

        dstElement.style.fontFamily = srcStyle.fontFamily;
        dstElement.style.fontSize = srcStyle.fontSize;
        dstElement.style.fontStyle = srcStyle.fontStyle;
        dstElement.style.fontWeight = srcStyle.fontWeight;
        dstElement.style.color = srcStyle.color;
    }

    private getItemByNode(node: Node, offset: number, saveGlobalOffsetObj?: { offset: number }): ITxtmapItem | null {
        if (!this.textMap.length) {
            return null;
        }

        const walker = this.getTreeWalker(NodeFilter.SHOW_TEXT);

        let resultItem: ITxtmapItem | null = null;
        let globalOffset = 0; // first symbol position from begin of text

        while (walker.nextNode() !== null && !walker.currentNode.isSameNode(node)) {
            if (node && node.textContent && node.nodeType !== Node.TEXT_NODE &&
                walker.currentNode.compareDocumentPosition(node) !== Node.DOCUMENT_POSITION_FOLLOWING) {
                break;
            }

            if (walker.currentNode && walker.currentNode.textContent) {
                globalOffset += walker.currentNode.textContent.length;
            }
        }

        const wholeOffset = globalOffset + offset;

        for (const item of this.txtMap) {
            if (wholeOffset >= item.symbPos && wholeOffset < (item.symbPos + item.txtSize)) { // is item inside calculated position
                resultItem = item;
                break;
            }
        }

        if (saveGlobalOffsetObj) {
            saveGlobalOffsetObj.offset = wholeOffset;
        }

        return resultItem;
    }

    private buildTxtMap(): ITxtmapItem[] {
        let globalSymbPos = 0;
        let globalWrdIdx = -1;
        let sntncIndex = 0;

        const mapItms: ITxtmapItem[] = [];
        const txtData = this.splitText(this.extractEtireText());
        const paragraphs = new Set(txtData.paragraphs);

        for (const txtItem of txtData.text) {
            const isSpace = (txtItem.length === 0);
            const isNewLine = txtItem.length === 1 && txtItem === '\n';
            const symbType = this.getSymbType(txtItem);
            const lastMapItem = mapItms[mapItms.length - 1];
            const ndForApnd = mapItms.length > 0 && lastMapItem.type !== TxtItemType.Word && symbType !== TxtItemType.Word;
            const isCstmParagraph = paragraphs.has(globalSymbPos);
            const startNewSntnc = isCstmParagraph || mapItms.length > 0 && symbType === TxtItemType.Word &&
                (isEndOfSntcSymb(lastMapItem.content) || lastMapItem.sntncIndex === -1);

            if (mapItms.length > 0 && lastMapItem.txtSize === -1) {
                if (isCstmParagraph || isSpace || isNewLine || symbType === TxtItemType.Word) {
                    lastMapItem.txtSize = lastMapItem.content.length;
                } else {
                    globalSymbPos += txtItem.length;
                    this.appendSymbolToMap(mapItms, txtItem, symbType);
                    continue;
                }
            }

            sntncIndex += startNewSntnc && mapItms.length > 0 && mapItms[mapItms.length - 1].sntncIndex === sntncIndex ? 1 : 0;

            if (!isSpace && !isNewLine) { // word found
                const itmLang = txtData.languages.find(x => globalSymbPos >= x.begin && (globalSymbPos + txtItem.length) <= x.end) || null;

                mapItms.push({
                    symbPos: globalSymbPos, itemIndex: ++globalWrdIdx, type: symbType, content: txtItem, sntncIndex,
                    txtSize: ndForApnd ? -1 : txtItem.length, status: SpellCheckWordStatus.PROCESSING,
                    lang: itmLang === null ? '' : itmLang.lang
                });
                globalSymbPos += txtItem.length;
            } else { // space or new line found
                globalSymbPos++;
            }
        }
        return mapItms;
    }

    private extractEtireText(): { text: string, paragraphs: number[], languages: IDictLanguage[] } {
        const walker = this.getTreeWalker(NodeFilter.SHOW_ALL);
        const paragraphs: number[] = [];
        const languages: IDictLanguage[] = [];
        let globalSymbPos = 0;
        let rootElement: Node | null = null;
        let text = '';

        while (walker.nextNode() !== null) { // iterate every node in current editor boundary
            if (walker.currentNode) {
                const currTag = walker.currentNode.nodeType !== Node.TEXT_NODE && walker.currentNode.nodeType !== Node.COMMENT_NODE ?
                    (walker.currentNode as HTMLElement).tagName.toLowerCase() : '#txt';  // if no tag name, consider it as text
                // skip some wrappers (like lists 'ul') to process each item separately
                const isWrapperTag = currTag === 'ul' || currTag === 'ol' || currTag === 'details';

                // detect laguage of certain text for received lookup html output
                const lang = (walker.currentNode as HTMLElement).lang;
                if (currTag === 'info' && lang && lang.trim() !== '') {
                    const prevLang = languages.length === 0 ? null : languages[languages.length - 1];
                    const nextLang = {
                        lang: lang.trim(),
                        begin: globalSymbPos,
                        end: globalSymbPos + (walker.currentNode.textContent ? walker.currentNode.textContent.length : 0)
                    };

                    if (prevLang !== null && prevLang.end === nextLang.begin && prevLang.lang === nextLang.lang) {
                        prevLang.end = nextLang.end; // join several "info" elements with same language
                    } else {
                        languages.push(nextLang); // store begin and end position in text to assume it as certain language
                    }
                }

                globalSymbPos += currTag === '#txt' && walker.currentNode.textContent ? walker.currentNode.textContent.length : 0;

                // iterator gets each direct child of root node and then goes recusively through all it's children from higest to deepest
                if (!isWrapperTag && (rootElement === null || !rootElement.contains(walker.currentNode))) {
                    rootElement = walker.currentNode; // found direct child of root node
                    text += rootElement.textContent;
                }

                if ((currTag === 'p' || currTag === 'summary' || currTag === 'li' || currTag === 'br')
                    && !paragraphs.includes(globalSymbPos)) {
                    paragraphs.push(globalSymbPos); // store position where need to place line break
                }
            }
        }

        return { text, paragraphs: paragraphs.concat(), languages: languages.concat() };
    }

    private splitText(origTextData: ITxtDescription): ITxtDescription {
        const srcTxt = origTextData.text as string;
        const textArr = srcTxt.split(/[^\S\r\n]/);

        let resultArr: Array<string> = [];
        let startSpacesNum = 0;
        let endSpacesNum = 0;

        if (srcTxt.trim() === '') {
            return {
                text: new Array(srcTxt.length).fill(''), paragraphs: origTextData.paragraphs, languages: origTextData.languages.concat()
            };
        }

        // clean and count first and last spaces, to simplify processing spaces between words
        if (textArr[0] === '') {
            while (textArr[startSpacesNum] === '' && ++startSpacesNum < textArr.length) { }
            textArr.splice(0, startSpacesNum);
        }

        if (textArr[textArr.length - 1] === '') {
            while (textArr[textArr.length - (endSpacesNum + 1)] === '' && (endSpacesNum + 1) < textArr.length) { endSpacesNum++; }
            textArr.splice(textArr.length - endSpacesNum, endSpacesNum);
        }

        for (let i = 0; i < textArr.length; i++) {
            const txtItem = textArr[i];

            if (isEnglishAbbreviation(txtItem)) {
                resultArr.push(txtItem);
            } else {
                const pnctSigns = txtItem.split(getPunctSignsRegex());
                for (const spltSign of pnctSigns) {
                    if (spltSign !== '') {
                        resultArr.push(spltSign);
                    }
                }
            }

            // because "string.split" leaves on one space less between words
            if (i !== textArr.length - 1) {
                resultArr.push('');
            }
        }

        // add start and finish spaces back
        if (startSpacesNum > 0) {
            resultArr = new Array(startSpacesNum).fill('').concat(resultArr);
        }

        if (endSpacesNum > 0) {
            new Array(endSpacesNum).fill('').forEach(x => resultArr.push(x));
        }

        // words which are separated only by line breaks (without spaces) will be joined if skip this call
        return this.splitWordsByParagraph(resultArr, origTextData.paragraphs.concat(), origTextData.languages.concat());
    }

    private splitWordsByParagraph(text: string[], paragraphs: number[], languages: IDictLanguage[]): ITxtDescription {
        const parList = paragraphs.concat();
        const resultTextArr: Array<string> = [];
        let globalSymbPos = 0;
        let currentParPos = parList.shift();

        while (currentParPos !== undefined && globalSymbPos >= currentParPos && parList.length > 0) {
            currentParPos = parList.shift();
        }

        for (let i = 0; i < text.length; i++) {
            let txtItm = text[i];
            if (currentParPos !== undefined && currentParPos > globalSymbPos && currentParPos < globalSymbPos + txtItm.length) {
                const firstPart = txtItm.substring(0, currentParPos - globalSymbPos);
                const lastPart = txtItm.substring(currentParPos - globalSymbPos);
                text.splice(i, 1, firstPart, lastPart);
                txtItm = firstPart;
            }

            resultTextArr.push(text[i]);
            globalSymbPos += text[i].length === 0 ? 1 : text[i].length;

            while (currentParPos !== undefined && globalSymbPos >= currentParPos && parList.length > 0) {
                currentParPos = parList.shift();
            }
        }

        return { text: resultTextArr, paragraphs: paragraphs.concat(), languages: languages.concat() };
    }

    private appendSymbolToMap(mapItems: ITxtmapItem[], symb: string, symbType: TxtItemType): void {
        if (mapItems.length === 0 || mapItems[mapItems.length - 1].txtSize >= 0 || symb.length !== 1) {
            return;
        }

        const prevItm = mapItems[mapItems.length - 1];

        if (symbType === TxtItemType.Punctuation && prevItm.type === TxtItemType.Punctuation) {
            if (isEndOfSntcSymb(prevItm.content)) {
                prevItm.sntncIndex = -1;
            }

            prevItm.content += symb;
        }

    }

    private getSymbType(symb: string): TxtItemType {
        if (punctSigns.lastIndexOf(symb) !== -1) {
            return TxtItemType.Punctuation;
        } else if (symb.search(/\d/) === 0) {
            return TxtItemType.SingleDigit;
        } else {
            return TxtItemType.Word;
        }
    }

    private getTreeWalker(whatToShow: number, nodeToSelect: Node | null = null): TreeWalker {
        const treeWalker = this.targetDoc.createTreeWalker(this.rootNode, whatToShow, { acceptNode: this.defaultNodesFilter });

        if (nodeToSelect !== null) {
            // iterate to desired node
            while (treeWalker.nextNode() !== null && !treeWalker.currentNode.isSameNode(nodeToSelect)) { }
        }

        return treeWalker;
    }

    private defaultNodesFilter(node: Node): number {
        const element = node.nodeType === Node.ELEMENT_NODE ? node as HTMLElement : node.parentElement;

        if (element !== null) {
            const tagName = element.tagName.toLowerCase();

            switch (tagName) {
                case 'meta':
                case 'style':
                case 'script':
                    return NodeFilter.FILTER_REJECT;
                default: break;
            }

            const detailsEl = element.closest('details');
            if (detailsEl !== null && tagName !== 'details' && !element.closest('summary')) {
                if (!detailsEl.open) {
                    return NodeFilter.FILTER_REJECT;
                }
            }
        }

        return NodeFilter.FILTER_ACCEPT;
    }
}

export function isEndOfSntcSymb(txtItem: string): boolean {
    return txtItem.length === 1 && (txtItem === '.' || txtItem === '!' || txtItem === '?');
}

export function getPunctSignsRegex(): RegExp {
    const regexStr = punctSigns.map(x => `\\${x}`).join('|')
        + '|\n' // processing end of line separately
        + '|^\-|\-$'; // separate rule for hyphens (considering "hyphenated-word" like one solid word)
    return new RegExp(`(${regexStr})`);
}

export const isEnglishAbbreviation = (word: string): boolean => {
    return titles.has(word.toLowerCase());
};

export function getCaretPostion(contenteditableEl: HTMLElement | null, parentWindow: Window | null): { start: number, end: number } | null {
    const selection = parentWindow && parentWindow.getSelection();

    if (contenteditableEl !== null && selection !== null && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);

        return {
            start: getTextLength(contenteditableEl, range.startContainer, range.startOffset),
            end: getTextLength(contenteditableEl, range.endContainer, range.endOffset)
        };
    } else {
        return null;
    }
}

export function getTextLength(parent: HTMLElement, node: Node, offset: number): number {
    let textLength = 0;

    if (node.nodeName === '#text') {
        textLength += offset;
    } else {
        for (let i = 0; i < offset; i++) {
            textLength += getNodeTextLength(node.childNodes[i]);
        }
    }

    if (node !== parent) {
        textLength += getTextLength(parent, node.parentNode as Node, getNodeOffset(node));
    }

    return textLength;
}


export function getNodeTextLength(node: Node): number {
    let textLength = 0;

    if (node.nodeName === 'BR') {
        textLength = 1;
    } else if (node.nodeValue && node.nodeName === '#text') {
        textLength = node.nodeValue.length;
    } else if (node.childNodes !== null) {
        for (var i = 0; i < node.childNodes.length; i++) {
            textLength += getNodeTextLength(node.childNodes[i]);
        }
    }

    return textLength;
}

export function getNodeOffset(node: Node | null): number {
    return node == null ? -1 : 1 + getNodeOffset(node.previousSibling);
}
